import { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router';
import { useLocation } from 'react-router-dom';
import { Toast } from '@belong/ui';
import { MaintenanceModel, MaintenanceRecurrenceModel } from 'api/models';
import String from 'components/String/String';
import { useFeatureFlag } from 'hooks/use-account-redesign';
import { BookAProButton } from 'maintenance/components/book-a-pro-button/book-a-pro-button';
import { TaskList } from 'maintenance/components/task-list/task-list';
import { URL_QUERY_PARAMS } from 'maintenance/constants';
import { STRINGS } from 'maintenance/strings';
import RenterAccountLease from 'models/renterAccounts/RenterAccountLease';
import { useMaintenanceAgentHistory } from 'pages/Accounts/Components/maintenance-agent/maintenance-agent.hooks';
import AccountPage from 'pages/Accounts/Pages/AccountPage/AccountPage';
import { PATHS } from 'routes/paths';
import {
  fetchRecurringTasks,
  fetchResidentMaintenance,
} from 'store/redux/maintenance/actions';
import { selectLeases } from 'store/redux/renter-accounts/selectors';
import { selectUser } from 'store/redux/user/selectors';
import { RENTER_ACCOUNTS_STRINGS } from 'strings/renter-accounts.strings';
import {
  LEASE_STATUS,
  getLeaseStatus,
} from 'utils/formateDate/getTimeForLeasingStatus';
import { MaintenanceAgentHistory } from './MaintenanceAgent/maintenance-agent-history';

const RAC = RENTER_ACCOUNTS_STRINGS.maintenance;

export function ResidentMaintenance() {
  const dispatch = useDispatch();
  const history = useHistory();
  const { pathname, search } = useLocation();
  const { replace } = useHistory();
  const [tasks, setTasks] = useState<MaintenanceModel[]>([]);
  const [recurringTasks, setRecurringTasks] = useState<
    MaintenanceRecurrenceModel[]
  >([]);
  const [isLoading, setIsLoading] = useState(false);
  const leases = useSelector<unknown, RenterAccountLease[]>(selectLeases);
  const currentLease = leases?.filter(
    (lease) => getLeaseStatus(lease) === LEASE_STATUS.CURRENT,
  )?.[0];
  const user = useSelector(selectUser);
  const { isEnabled: isMaintenanceAgentEnabled } =
    useFeatureFlag('maintenance-agent');
  const {
    threads,
    isLoading: isLoadingThreads,
    refetch: refetchThreads,
  } = useMaintenanceAgentHistory({
    // `selectUser` is nullable, but this page only renders for a signed-in
    // resident and the read was already unconditional.
    userId: user!.id,
    enabled: isMaintenanceAgentEnabled,
  });

  useEffect(() => {
    const fetchMaintenanceDetails = async () => {
      try {
        setIsLoading(true);
        const nonRecurringTasksResponse = await dispatch(
          fetchResidentMaintenance(),
        );
        const recurringTasksResponse = await dispatch(fetchRecurringTasks());
        setTasks(nonRecurringTasksResponse);
        setRecurringTasks(recurringTasksResponse);
      } catch (e) {
        console.log(e);
      } finally {
        setIsLoading(false);
      }
    };

    fetchMaintenanceDetails();
  }, [dispatch]);

  const showMaintenanceModal = (task: any) => (appointmentId?: string) => {
    // `appointmentId` is only a string when navigating from the work-completion
    // prompt; a plain card click invokes this with the click event, so guard.
    const outcomeParam = appointmentId
      ? `?${URL_QUERY_PARAMS.OUTCOME}=${appointmentId}`
      : '';

    history.push(
      `${PATHS.RESIDENTS_ACCOUNT_MAINTENANCE}/${task.uniqueId}${outcomeParam}`,
    );
  };
  const showRecurringMaintenanceModal = (task: any) => () => {
    history.push(
      `${PATHS.RESIDENTS_ACCOUNT_RECURRING_MAINTENANCE}/${task.homeUniqueId}/${task.recurringTask}`,
    );
  };

  // Derived from the URL so closing the toast just strips the query params.
  const isToastVisible =
    new URLSearchParams(search).get(URL_QUERY_PARAMS.TOAST_VISIBLE) === 'true';

  const handleToastClose = () => {
    replace(pathname);
  };

  const getToastText = () => {
    const parsedURL = new URLSearchParams(search);

    const successURLParam = parsedURL.get(URL_QUERY_PARAMS.SUCCESS);
    const creationURLParam = parsedURL.get(URL_QUERY_PARAMS.CREATION);
    const turnOffURLParam = parsedURL.get(URL_QUERY_PARAMS.TURN_OFF);
    const updateFrequencyURLParam = parsedURL.get(
      URL_QUERY_PARAMS.UPDATE_FREQUENCY,
    );
    const updateURLParam = parsedURL.get(URL_QUERY_PARAMS.UPDATE);

    const isSuccessToast = successURLParam === 'true';

    let toastText = '';

    if (isSuccessToast) {
      if (creationURLParam) {
        toastText = STRINGS['submitted-pro-service'];
      }

      if (turnOffURLParam) {
        toastText = STRINGS['turn-off-recurring'];
      }

      if (updateFrequencyURLParam) {
        toastText = STRINGS['change-frequency'];
      }

      if (updateURLParam) {
        toastText = STRINGS['updated-pro-service'];
      }
    }
    return toastText;
  };

  return (
    <>
      <AccountPage
        loading={isLoading || isLoadingThreads}
        headerProps={{
          headerText: RAC.title,
          subtitle: <String string={RAC.subtitle} />,
        }}
        cta={
          <div className="hidden items-center justify-end sm:ml-sm sm:flex">
            <BookAProButton
              userRole="Resident"
              currentLeaseUnitId={currentLease?.unitInfo.unitId}
              containerClassName="hidden sm:block"
            />
          </div>
        }
      >
        {isMaintenanceAgentEnabled && (
          <div className="mb-2xl">
            <MaintenanceAgentHistory
              unitId={currentLease?.unitInfo.unitId}
              threads={threads}
              refetchThreads={refetchThreads}
            />
          </div>
        )}

        <div className="mb-2xl">
          <TaskList
            showMaintenanceModal={showMaintenanceModal}
            tasks={tasks}
            type="non-recurring"
            view="resident"
          />
        </div>

        <div className="mb-6xl">
          <TaskList
            showMaintenanceModal={showRecurringMaintenanceModal}
            tasks={recurringTasks}
            type="recurring"
            view="resident"
          />
        </div>

        <BookAProButton
          userRole="Resident"
          currentLeaseUnitId={currentLease?.unitInfo.unitId}
          containerClassName="fixed sm:hidden"
        />

        <Toast
          isVisible={isToastVisible}
          onClose={handleToastClose}
          autocloseDuration={3000}
        >
          <div className="sm:max-w-[530px]">{getToastText()}</div>
        </Toast>
      </AccountPage>
    </>
  );
}
